import { useMemo, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { ArrowRight, Check, ClipboardList, Minus, Plus, Printer, X } from "lucide-react";
import { AVO, CATALOG, CATALOG_CATEGORIES, WOOD_GRADES } from "@/data/content";
import { Reveal } from "@/components/Motion";

const Chip = ({ active, onClick, children, testId }) => (
  <button
    data-testid={testId}
    onClick={onClick}
    className={`rounded-full border px-3.5 py-2 font-mono text-[10px] uppercase tracking-[0.15em] transition-colors ${
      active
        ? "border-[#F4B032] bg-[#F4B032] text-[#0F0E0C]"
        : "border-[#2A2723] text-[#A59E96] hover:border-[#A59E96]"
    }`}
  >
    {children}
  </button>
);

const Stepper = ({ value, onChange, min = 0, max = 9, testId }) => (
  <div className="inline-flex items-center gap-3 rounded-full border border-[#2A2723] px-2 py-1.5">
    <button data-testid={`${testId}-minus`} onClick={() => onChange(Math.max(min, value - 1))} className="flex h-7 w-7 items-center justify-center rounded-full text-[#A59E96] transition-colors hover:bg-[#2A2723] hover:text-[#F1F1F1]">
      <Minus className="h-3.5 w-3.5" />
    </button>
    <span className="w-6 text-center font-condensed text-xl font-bold text-[#F1F1F1]">{value}</span>
    <button data-testid={`${testId}-plus`} onClick={() => onChange(Math.min(max, value + 1))} className="flex h-7 w-7 items-center justify-center rounded-full text-[#A59E96] transition-colors hover:bg-[#2A2723] hover:text-[#F1F1F1]">
      <Plus className="h-3.5 w-3.5" />
    </button>
  </div>
);

const Configurator = ({ item, onClose, onAdd }) => {
  const [sel, setSel] = useState(() =>
    Object.fromEntries(item.groups.map((g) => [g.key, g.options[0]]))
  );
  const [footage, setFootage] = useState(96);
  const [qty, setQty] = useState(1);
  const [gates, setGates] = useState(0);
  const [grade, setGrade] = useState("Premium");
  const [post, setPost] = useState(item.posts ? item.posts[0] : null);
  const [cap, setCap] = useState(item.caps ? item.caps[0] : null);

  const add = () => {
    onAdd({
      code: item.code,
      name: item.name,
      options: Object.entries(sel).map(([k, v]) => [k, v]),
      footage: item.hasFootage ? footage : null,
      qty: item.hasQty ? qty : null,
      gates: item.hasGates ? gates : null,
      grade: item.grades ? grade : null,
      post,
      cap,
    });
  };

  return (
    <motion.div
      data-testid="configurator-panel"
      data-lenis-prevent
      initial={{ x: "100%" }}
      animate={{ x: 0 }}
      exit={{ x: "100%" }}
      transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
      className="fixed inset-y-0 right-0 z-40 w-full overflow-y-auto border-l border-[#2A2723] bg-[#0F0E0C] sm:w-[480px]"
    >
      <div className="relative">
        <img src={item.image} alt={item.name} className="h-52 w-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#0F0E0C] to-transparent" />
        <button data-testid="configurator-close-button" onClick={onClose} className="absolute right-4 top-4 flex h-9 w-9 items-center justify-center rounded-full bg-black/60 text-[#F1F1F1] backdrop-blur transition-colors hover:bg-[#F4B032] hover:text-[#0F0E0C]">
          <X className="h-4 w-4" />
        </button>
        <div className="absolute bottom-4 left-6">
          <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#F4B032]">{item.code} · {item.category}</p>
          <h3 className="mt-1 font-condensed text-3xl font-extrabold uppercase tracking-tight text-[#F1F1F1]">{item.name}</h3>
        </div>
      </div>

      <div className="space-y-7 p-6 pb-28">
        <div className="grid grid-cols-2 gap-3 border border-[#2A2723] bg-[#181614] p-4">
          {item.specs.map(([k, v]) => (
            <div key={k}>
              <p className="font-mono text-[9px] uppercase tracking-[0.2em] text-[#A59E96]">{k}</p>
              <p className="mt-0.5 font-jakarta text-xs text-[#F1F1F1]">{v}</p>
            </div>
          ))}
        </div>

        {item.groups.map((g) => (
          <div key={g.key}>
            <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#A59E96]">{g.label}</p>
            <div className="mt-3 flex flex-wrap gap-2">
              {g.options.map((o) => (
                <Chip key={o} testId={`config-${g.key}-${o.replace(/[^a-z0-9]/gi, "-").toLowerCase()}`} active={sel[g.key] === o} onClick={() => setSel({ ...sel, [g.key]: o })}>
                  {o}
                </Chip>
              ))}
            </div>
          </div>
        ))}

        {item.grades && (
          <div>
            <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#A59E96]">Wood Grade</p>
            <div className="mt-3 space-y-2">
              {WOOD_GRADES.map((g) => (
                <button
                  key={g.id}
                  data-testid={`config-grade-${g.id.replace(/[^a-z0-9]/gi, "-").toLowerCase()}`}
                  onClick={() => setGrade(g.id)}
                  className={`flex w-full items-center justify-between border px-4 py-3 text-left transition-colors ${
                    grade === g.id ? "border-[#F4B032] bg-[#F4B032]/10" : "border-[#2A2723] hover:border-[#A59E96]"
                  }`}
                >
                  <span className={`font-condensed text-lg font-bold uppercase ${grade === g.id ? "text-[#F4B032]" : "text-[#F1F1F1]"}`}>{g.id}</span>
                  <span className="text-right font-jakarta text-[11px] text-[#A59E96]">{g.note}</span>
                </button>
              ))}
            </div>
          </div>
        )}

        {item.hasFootage && (
          <div>
            <div className="flex items-baseline justify-between">
              <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#A59E96]">Linear footage</p>
              <p className="font-condensed text-4xl font-extrabold text-[#F1F1F1]">{footage}<span className="text-[#F4B032]">ft</span></p>
            </div>
            <input data-testid="config-footage-slider" type="range" min="8" max="400" step="8" value={footage} onChange={(e) => setFootage(Number(e.target.value))} className="mt-4 w-full accent-[#F4B032]" />
          </div>
        )}

        {item.hasQty && (
          <div className="flex items-center justify-between">
            <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#A59E96]">Quantity</p>
            <Stepper value={qty} onChange={setQty} min={1} testId="config-qty" />
          </div>
        )}

        {item.hasGates && (
          <div className="flex items-center justify-between">
            <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#A59E96]">Matching gates</p>
            <Stepper value={gates} onChange={setGates} testId="config-gates" />
          </div>
        )}

        {item.posts && (
          <div>
            <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#A59E96]">Posts</p>
            <div className="mt-3 flex flex-wrap gap-2">
              {item.posts.map((p) => (
                <Chip key={p} testId={`config-post-${p.replace(/[^a-z0-9]/gi, "-").toLowerCase()}`} active={post === p} onClick={() => setPost(p)}>{p}</Chip>
              ))}
            </div>
          </div>
        )}

        {item.caps && (
          <div>
            <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#A59E96]">Post caps</p>
            <div className="mt-3 flex flex-wrap gap-2">
              {item.caps.map((c) => (
                <Chip key={c} testId={`config-cap-${c.replace(/[^a-z0-9]/gi, "-").toLowerCase()}`} active={cap === c} onClick={() => setCap(c)}>{c}</Chip>
              ))}
            </div>
          </div>
        )}

        <button
          data-testid="configurator-add-button"
          onClick={add}
          className="group flex w-full items-center justify-center gap-3 rounded-full bg-[#F4B032] py-4 font-mono text-xs font-bold uppercase tracking-[0.25em] text-[#0F0E0C] transition-colors hover:bg-[#FFC14D]"
        >
          Add to RFQ <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
        </button>
      </div>
    </motion.div>
  );
};

const RfqDrawer = ({ items, onClose, onRemove, onDone }) => {
  const [name, setName] = useState("");
  const [company, setCompany] = useState("");
  const [contact, setContact] = useState("");
  const [ref, setRef] = useState(null);

  const totalFt = items.reduce((s, i) => s + (i.footage || 0), 0);

  const submit = () => {
    setRef(`AV-2026-${String(Math.floor(1000 + Math.random() * 9000))}`);
  };

  return (
    <motion.div
      data-testid="rfq-drawer"
      data-lenis-prevent
      initial={{ x: "100%" }}
      animate={{ x: 0 }}
      exit={{ x: "100%" }}
      transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
      className="fixed inset-y-0 right-0 z-40 flex w-full flex-col border-l border-[#2A2723] bg-[#0F0E0C] sm:w-[480px]"
    >
      <div className="flex items-center justify-between border-b border-[#2A2723] p-6">
        <div>
          <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#F4B032]">Request for Quote</p>
          <h3 className="mt-1 font-condensed text-2xl font-extrabold uppercase text-[#F1F1F1]">Your Spec Sheet</h3>
        </div>
        <button data-testid="rfq-close-button" onClick={onClose} className="flex h-9 w-9 items-center justify-center rounded-full border border-[#2A2723] text-[#A59E96] transition-colors hover:border-[#F4B032] hover:text-[#F4B032]">
          <X className="h-4 w-4" />
        </button>
      </div>

      {ref ? (
        <div className="flex flex-1 flex-col overflow-y-auto p-6">
          <div className="flex flex-col items-center pt-4 text-center">
            <span className="flex h-14 w-14 items-center justify-center rounded-full bg-[#F4B032]">
              <Check className="h-6 w-6 text-[#0F0E0C]" />
            </span>
            <h4 data-testid="rfq-success-message" className="mt-5 font-condensed text-3xl font-extrabold uppercase text-[#F1F1F1]">RFQ Submitted</h4>
            <p className="mt-2 font-mono text-sm tracking-[0.2em] text-[#F4B032]">REF {ref}</p>
            <p className="mt-3 max-w-xs font-jakarta text-sm text-[#A59E96]">
              The AVO yard team will call back with mill-direct pricing within one business day.
            </p>
          </div>
          <div className="mt-8 border border-[#2A2723]">
            <p className="border-b border-[#2A2723] bg-[#181614] px-4 py-2.5 font-mono text-[10px] uppercase tracking-[0.25em] text-[#A59E96]">Spec summary</p>
            {items.map((it, i) => (
              <div key={i} className="border-b border-[#2A2723] px-4 py-3 last:border-0">
                <p className="font-condensed text-lg font-bold uppercase text-[#F1F1F1]">{it.name}</p>
                <p className="mt-0.5 font-mono text-[10px] uppercase tracking-[0.15em] text-[#A59E96]">
                  {it.options.map(([, v]) => v).join(" · ")}
                  {it.grade ? ` · ${it.grade}` : ""}
                  {it.footage ? ` · ${it.footage} ft` : ""}
                  {it.qty ? ` · Qty ${it.qty}` : ""}
                  {it.gates ? ` · ${it.gates} gate${it.gates > 1 ? "s" : ""}` : ""}
                </p>
              </div>
            ))}
          </div>
          <div className="mt-6 flex gap-3 pb-4">
            <button data-testid="rfq-print-button" onClick={() => window.print()} className="flex flex-1 items-center justify-center gap-2 rounded-full border border-[#2A2723] py-3.5 font-mono text-[11px] uppercase tracking-[0.2em] text-[#F1F1F1] transition-colors hover:border-[#F4B032] hover:text-[#F4B032]">
              <Printer className="h-4 w-4" /> Print Spec Sheet
            </button>
            <button data-testid="rfq-done-button" onClick={onDone} className="flex-1 rounded-full bg-[#F4B032] py-3.5 font-mono text-[11px] font-bold uppercase tracking-[0.2em] text-[#0F0E0C] transition-colors hover:bg-[#FFC14D]">
              Done
            </button>
          </div>
        </div>
      ) : (
        <>
          <div className="flex-1 space-y-3 overflow-y-auto p-6">
            {items.length === 0 && (
              <p className="pt-10 text-center font-jakarta text-sm text-[#A59E96]">
                Your spec sheet is empty. Configure a style from the catalog to add it here.
              </p>
            )}
            {items.map((it, i) => (
              <div key={i} data-testid={`rfq-item-${i}`} className="flex items-start justify-between gap-3 border border-[#2A2723] bg-[#181614] p-4">
                <div>
                  <p className="font-mono text-[9px] uppercase tracking-[0.2em] text-[#F4B032]">{it.code}</p>
                  <p className="mt-0.5 font-condensed text-xl font-bold uppercase text-[#F1F1F1]">{it.name}</p>
                  <p className="mt-1 font-mono text-[10px] uppercase tracking-[0.12em] text-[#A59E96]">
                    {it.options.map(([, v]) => v).join(" · ")}
                    {it.grade ? ` · ${it.grade}` : ""}
                    {it.footage ? ` · ${it.footage} ft` : ""}
                    {it.qty ? ` · Qty ${it.qty}` : ""}
                    {it.gates ? ` · ${it.gates} gate${it.gates > 1 ? "s" : ""}` : ""}
                    {it.post ? ` · ${it.post}` : ""}
                  </p>
                </div>
                <button data-testid={`rfq-remove-${i}`} onClick={() => onRemove(i)} className="text-[#A59E96] transition-colors hover:text-[#F4B032]">
                  <X className="h-4 w-4" />
                </button>
              </div>
            ))}
          </div>
          <div className="space-y-4 border-t border-[#2A2723] p-6">
            {totalFt > 0 && (
              <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#A59E96]">
                Total fencing: <span className="text-[#F4B032]">{totalFt} linear ft</span>
              </p>
            )}
            <input data-testid="rfq-name-input" placeholder="Your name" value={name} onChange={(e) => setName(e.target.value)} className="w-full border border-[#2A2723] bg-[#0F0E0C] px-4 py-3 font-jakarta text-sm text-[#F1F1F1] placeholder:text-[#A59E96]/60 focus:border-[#F4B032] focus:outline-none" />
            <input data-testid="rfq-company-input" placeholder="Company (contractors)" value={company} onChange={(e) => setCompany(e.target.value)} className="w-full border border-[#2A2723] bg-[#0F0E0C] px-4 py-3 font-jakarta text-sm text-[#F1F1F1] placeholder:text-[#A59E96]/60 focus:border-[#F4B032] focus:outline-none" />
            <input data-testid="rfq-contact-input" placeholder="Phone or email" value={contact} onChange={(e) => setContact(e.target.value)} className="w-full border border-[#2A2723] bg-[#0F0E0C] px-4 py-3 font-jakarta text-sm text-[#F1F1F1] placeholder:text-[#A59E96]/60 focus:border-[#F4B032] focus:outline-none" />
            <button
              data-testid="rfq-submit-button"
              onClick={submit}
              disabled={items.length === 0}
              className="flex w-full items-center justify-center gap-2 rounded-full bg-[#F4B032] py-4 font-mono text-xs font-bold uppercase tracking-[0.25em] text-[#0F0E0C] transition-colors hover:bg-[#FFC14D] disabled:opacity-40"
            >
              Submit RFQ to AVO <ArrowRight className="h-4 w-4" />
            </button>
          </div>
        </>
      )}
    </motion.div>
  );
};

const CatalogRFQ = ({ products }) => {
  const [cat, setCat] = useState("Cedar Fences");
  const [configuring, setConfiguring] = useState(null);
  const [rfq, setRfq] = useState([]);
  const [rfqOpen, setRfqOpen] = useState(false);

  const items = useMemo(() => products.filter((c) => c.category === cat && c.visible !== false), [products, cat]);

  return (
    <div data-testid="catalog-page" className="min-h-screen bg-[#0F0E0C] pb-32">
      <div className="grain-overlay" />

      <header className="flex items-center justify-between border-b border-[#2A2723] px-6 py-5 md:px-12">
        <img src="/avo-logo.png" alt="AVO Fence & Supply" className="h-8 w-auto" data-testid="catalog-logo" />
        <button
          data-testid="rfq-open-button"
          onClick={() => setRfqOpen(true)}
          className="relative inline-flex items-center gap-2 rounded-full border border-[#2A2723] px-5 py-2.5 font-mono text-[11px] uppercase tracking-[0.2em] text-[#F1F1F1] transition-colors hover:border-[#F4B032] hover:text-[#F4B032]"
        >
          <ClipboardList className="h-4 w-4" /> RFQ
          {rfq.length > 0 && (
            <span data-testid="rfq-count-badge" className="flex h-5 w-5 items-center justify-center rounded-full bg-[#F4B032] font-bold text-[#0F0E0C]">{rfq.length}</span>
          )}
        </button>
      </header>

      <section className="px-6 pb-10 pt-16 md:px-12 md:pt-20">
        <Reveal>
          <p className="font-mono text-[11px] uppercase tracking-[0.35em] text-[#F4B032]">AVO Materials — Interactive Spec Catalog</p>
          <h1 className="mt-4 max-w-4xl font-condensed text-5xl font-extrabold uppercase leading-[0.95] tracking-tight text-[#F1F1F1] md:text-7xl">
            Build your spec. Get your quote<span className="text-[#F4B032]">.</span>
          </h1>
          <p className="mt-6 max-w-xl font-jakarta text-sm leading-relaxed text-[#A59E96] md:text-base">
            Every fence style, rail profile, post and cap from the AVO yard — configurable online.
            Pick a style, spec your height, grade and footage, and send the yard a complete RFQ in
            under a minute.
          </p>
          <p className="mt-4 inline-block border border-[#F4B032]/40 bg-[#F4B032]/10 px-3 py-1.5 font-mono text-[9px] uppercase tracking-[0.25em] text-[#F4B032]">
            Prototype — submissions are simulated for this presentation
          </p>
        </Reveal>
      </section>

      <nav className="flex flex-wrap gap-2 border-y border-[#2A2723] px-6 py-4 md:px-12">
        {CATALOG_CATEGORIES.map((c) => (
          <button
            key={c}
            data-testid={`catalog-category-tab-${c.replace(/[^a-z0-9]/gi, "-").toLowerCase()}`}
            onClick={() => setCat(c)}
            className={`rounded-full px-4 py-2 font-mono text-[10px] uppercase tracking-[0.2em] transition-colors ${
              cat === c ? "bg-[#F4B032] font-bold text-[#0F0E0C]" : "text-[#A59E96] hover:text-[#F1F1F1]"
            }`}
          >
            {c}
          </button>
        ))}
      </nav>

      <section className="grid gap-[2px] bg-[#2A2723] px-0 md:grid-cols-2 xl:grid-cols-3">
        <AnimatePresence mode="popLayout">
          {items.map((item, i) => (
            <motion.article
              layout
              key={item.id}
              data-testid={`catalog-style-card-${item.id}`}
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.5, delay: i * 0.05 }}
              className="group bg-[#0F0E0C] p-6"
            >
              <div className="relative overflow-hidden">
                <img src={item.image} alt={item.name} className={`h-56 w-full object-cover transition-transform duration-700 ease-out group-hover:scale-105 ${item.status === "Out of Stock" ? "opacity-50 grayscale" : ""}`} />
                <span className="absolute left-3 top-3 bg-[#0F0E0C]/85 px-2.5 py-1 font-mono text-[9px] uppercase tracking-[0.25em] text-[#F4B032] backdrop-blur">{item.code}</span>
                {item.status && item.status !== "In Stock" && (
                  <span
                    data-testid={`catalog-status-${item.id}`}
                    className={`absolute right-3 top-3 px-2.5 py-1 font-mono text-[9px] uppercase tracking-[0.25em] backdrop-blur ${
                      item.status === "Out of Stock" ? "bg-red-950/85 text-red-300" : "bg-[#F4B032]/90 text-[#0F0E0C]"
                    }`}
                  >
                    {item.status}
                  </span>
                )}
              </div>
              <div className="mt-5 flex items-baseline justify-between gap-3">
                <h3 className="font-condensed text-2xl font-bold uppercase tracking-tight text-[#F1F1F1] transition-colors duration-500 group-hover:text-[#F4B032]">{item.name}</h3>
                {item.basePrice != null && (
                  <p className="whitespace-nowrap font-mono text-sm font-medium text-[#F4B032]">
                    ${item.basePrice}<span className="text-[10px] text-[#A59E96]">{item.hasFootage ? " /ft" : " ea"}</span>
                  </p>
                )}
              </div>
              <p className="mt-1 font-jakarta text-sm text-[#A59E96]">{item.tagline}</p>
              <ul className="mt-4 space-y-1.5 border-t border-[#2A2723] pt-4">
                {item.specs.slice(0, 3).map(([k, v]) => (
                  <li key={k} className="flex justify-between gap-4 font-jakarta text-xs">
                    <span className="uppercase tracking-[0.15em] text-[#A59E96]">{k}</span>
                    <span className="text-right text-[#F1F1F1]">{v}</span>
                  </li>
                ))}
              </ul>
              <button
                data-testid={`catalog-configure-${item.id}`}
                onClick={() => setConfiguring(item)}
                disabled={item.status === "Out of Stock"}
                className={`group/btn mt-5 inline-flex items-center gap-2 font-mono text-[11px] font-bold uppercase tracking-[0.25em] transition-colors ${
                  item.status === "Out of Stock"
                    ? "cursor-not-allowed text-[#A59E96]/50"
                    : "text-[#F4B032] hover:text-[#FFC14D]"
                }`}
              >
                {item.status === "Out of Stock" ? "Out of Stock" : "Configure & Add to RFQ"}
                {item.status !== "Out of Stock" && (
                  <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover/btn:translate-x-1" />
                )}
              </button>
            </motion.article>
          ))}
        </AnimatePresence>
      </section>

      <footer className="mt-16 border-t border-[#2A2723] px-6 py-8 md:px-12">
        <p className="font-jakarta text-xs leading-relaxed text-[#A59E96]">
          All fence panels fully customizable upon request · {AVO.address}, {AVO.city} · {AVO.phone}
        </p>
      </footer>

      <AnimatePresence>
        {configuring && (
          <>
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} onClick={() => setConfiguring(null)} className="fixed inset-0 z-30 bg-black/60 backdrop-blur-sm" />
            <Configurator
              item={configuring}
              onClose={() => setConfiguring(null)}
              onAdd={(entry) => {
                setRfq([...rfq, entry]);
                setConfiguring(null);
                setRfqOpen(true);
              }}
            />
          </>
        )}
        {rfqOpen && !configuring && (
          <>
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} onClick={() => setRfqOpen(false)} className="fixed inset-0 z-30 bg-black/60 backdrop-blur-sm" />
            <RfqDrawer
              items={rfq}
              onClose={() => setRfqOpen(false)}
              onRemove={(i) => setRfq(rfq.filter((_, x) => x !== i))}
              onDone={() => { setRfq([]); setRfqOpen(false); }}
            />
          </>
        )}
      </AnimatePresence>
    </div>
  );
};

export default CatalogRFQ;
